import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  SignatureType,
  SubmissionStatus,
  SubmissionVirtualEventsQuery,
  UserType,
} from "generated-graphql/graphql";
import { useAutoFlex } from "hooks/useAutoFlex";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import {
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { SUBMISSION_VIRTUAL_EVENTS_QUERY } from "queries/submissions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SubmissionBulkActionsMenu } from "./SubmissionBulkActionsMenu";
import { SubmissionsEmptyState } from "./SubmissionsEmptyState";
import { SubmissionReviewerCell } from "components/Submission/SubmissionReviewerCell";

export type Row =
  SubmissionVirtualEventsQuery["submissionVirtualEvents"]["items"][0];

export const STATUS_LABELS: Record<string, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

const TIME_FILTER_OPTIONS = {
  upcoming: "🔶 Upcoming",
  "in-progress": "💬 Current",
  completed: "🎉 Completed",
};

const getStatusLabel = (status: string): string => {
  return STATUS_LABELS[status] || status;
};

const TIME_FILTER_TITLES: Record<string, string> = {
  upcoming: "Upcoming Submissions",
  "in-progress": "Current Submissions",
  completed: "Completed Submissions",
};

const getTimeFilterTitle = (timeFilter: string | null): string | undefined => {
  if (!timeFilter) return undefined;
  return TIME_FILTER_TITLES[timeFilter];
};

export function WasteSubmissions() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { isStaff, user } = useCurrentUser();
  const currentUserEmail = user?.email;
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleRowClick = useCallback(
    (row: Row) => {
      const submissionId = row.submission?.id;
      if (!isStaff) {
        navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        return;
      }

      // Staff users: check submission status
      const isVirtualSubmission =
        row.submission?.status === SubmissionStatus.NotStarted;

      if (isVirtualSubmission) {
        // Navigate to create submission with taskId and startDate parameters
        const dateOnly = DateTime.fromISO(String(row.deadline)).toISODate();
        navigate(
          `/o/${tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`
        );
      } else {
        // Determine if submission should be viewed in read-only mode
        const status = row.submission?.status;
        const shouldViewReadOnly =
          status === SubmissionStatus.EncampInitialReview ||
          status === SubmissionStatus.InReview ||
          status === SubmissionStatus.FilingComplete ||
          status === SubmissionStatus.NotReporting;

        if (shouldViewReadOnly) {
          // Navigate to read-only view mode for:
          // - ENCAMP_INITIAL_REVIEW: Staff reviewer evaluates before sending to customers
          // - IN_REVIEW: Customers are actively reviewing/signing - staff should not be editing
          // - FILING_COMPLETE: Terminal state - nothing left to edit
          // - NOT_REPORTING: Opt-out status - rarely needs changes
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}`);
        } else {
          // Navigate to edit mode for:
          // - IN_PROGRESS: Staff actively works on draft
          // - PROCESSING_SUBMISSION: Staff is filing - may need to update metadata
          navigate(`/o/${tenantId}/waste/submissions/${submissionId}/edit`);
        }
      }
    },
    [isStaff, navigate, tenantId]
  );

  const handleAddSubmission = useCallback(() => {
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [navigate, tenantId]);

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Submissions" },
  ]);

  const omnisearchPersistenceKey = "submissions-list-view-v9";
  const autoFlex = useAutoFlex(
    1.0,
    {
      deadline: 0.1,
      title: 0.25,
      facilities: 0.1,
      "submission.status": 0.1,
      submissionType: 0.1,
      "submission.title": 0.1,
    },
    [
      "facilities",
      "submissionType",
      "title",
      "documents",
      "reviewers",
      "signer",
      "staffAssignee",
      "submission.status",
      "submission.title",
    ]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "deadline",
      headerName: "Due Date",
      sortable: true,
      flex: autoFlex.get("deadline"),
      minWidth: 20,
      renderCell: ({ value }) => {
        return (
          <CellText variant="body2">
            {DateTime.fromISO(value).toFormat("MM/dd/yyyy")}
          </CellText>
        );
      },
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: autoFlex.get("facilities"),
      minWidth: 20,
      sortable: true,
      renderCell({ row }) {
        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {(row.facilities ?? []).map((facility) => (
              <Typography
                key={facility.id}
                variant="body2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={facility.name}>
                  <span>{facility.name}</span>
                </Tooltip>
              </Typography>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "title",
      headerName: "Submission Type",
      flex: autoFlex.get("title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { title } }) => {
        return (
          <CellText variant="body2" noWrap>
            {/* For SYSTEM submissions, this is the pretty printed submission type
            For user submissions, this is the task title which is meant to be in sync with the submission title*/}
            {title}
          </CellText>
        );
      },
    },
    {
      field: "submission.title",
      headerName: "Submission Title",
      flex: autoFlex.get("submission.title"),
      minWidth: 100,
      sortable: true,
      renderCell: ({ row: { submission } }) => {
        return (
          <CellText variant="body2" noWrap>
            {submission?.title}
          </CellText>
        );
      },
    },

    {
      field: "notes",
      headerName: "Notes",
      flex: autoFlex.get("notes"),
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const notes = row.submission?.notes;
        if (!notes) return "";
        return (
          <CellText variant="body2" noWrap>
            {notes}
          </CellText>
        );
      },
    },
    {
      field: "documents",
      headerName: "Attachment(s)",
      flex: autoFlex.get("documents"),
      minWidth: 60,
      sortable: false,
      renderCell: (params) => {
        const documents = params.row.submission?.documents || [];

        if (documents.length === 0) return "";
        const mappedDocuments = documents
          .map((doc) => doc.document.title)
          .join(", ");
        return (
          <Tooltip title={mappedDocuments}>
            <CellText variant="body2">{mappedDocuments}</CellText>
          </Tooltip>
        );
      },
    },
    {
      field: "reviewers",
      headerName: "Reviewer(s)",
      flex: autoFlex.get("reviewers"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewMode = params.row.submission?.reviewMode;

        if (reviewers.length === 0) {
          return null;
        }

        return (
          <SubmissionReviewerCell
            reviewers={reviewers}
            reviewMode={reviewMode}
            isStaff={isStaff}
            type="reviewer"
          />
        );
      },
    },
    {
      field: "signer",
      headerName: "Signer",
      flex: autoFlex.get("signer"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const reviewers = params.row.submission?.reviewers || [];
        const reviewMode = params.row.submission?.reviewMode;
        const signers = reviewers.filter((r) => r.isSigner);
        const signatureType = params.row.submission?.signatureType;

        // Handle no signature required cases
        if (signatureType === SignatureType.None || signers.length === 0) {
          return (
            <CellText variant="body2" noWrap>
              No Signature Required
            </CellText>
          );
        }

        return (
          <SubmissionReviewerCell
            reviewers={reviewers}
            reviewMode={reviewMode}
            isStaff={isStaff}
            type="signer"
          />
        );
      },
    },
    {
      field: "staffAssignee",
      headerName: "Encamp Assignee",
      flex: autoFlex.get("staffAssignee"),
      minWidth: 90,
      renderCell: ({ row }) => {
        if (!row?.submission?.staffAssignee) {
          return null;
        }
        const person = row.submission.staffAssignee.person;
        if (!person) {
          return (
            <CellText variant="body2" noWrap>
              {row.submission.staffAssignee.email}
            </CellText>
          );
        }
        const cellText =
          `${person.first || ""} ${person.last || ""}`.trim() ||
          row.submission.staffAssignee.email;
        return (
          <CellText variant="body2" noWrap>
            {cellText}
          </CellText>
        );
      },
    },
    {
      field: "submission.status",
      headerName: "Status",
      flex: autoFlex.get("submission.status"),
      minWidth: 120,
      sortable: true,
      renderCell({ row: { submission } }) {
        return (
          <CellText variant="body2" noWrap>
            {getStatusLabel(submission?.status ?? SubmissionStatus.NotStarted)}
          </CellText>
        );
      },
    },
    {
      field: "waitingOn",
      headerName: "Waiting On",
      flex: autoFlex.get("waitingOn"),
      minWidth: 100,
      sortable: false,
      renderCell: (params) => {
        const waitingOn = params.row.waitingOn || [];

        if (waitingOn.length === 0) {
          return null;
        }

        return (
          <Stack
            spacing={0}
            style={{
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1),
              height: "100%",
              width: "100%",
              alignItems: "start",
              overflow: "hidden",
            }}
          >
            {waitingOn.map((user) => {
              const displayName =
                user.person?.first || user.person?.last
                  ? `${user.person.first || ""} ${
                      user.person.last || ""
                    }`.trim()
                  : user.email;

              return (
                <Typography
                  key={user.id || user.email}
                  variant="body2"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip title={user.email}>
                    <span>{displayName}</span>
                  </Tooltip>
                </Typography>
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      sortable: false,
      align: "right",
      getActions: ({ row }) => {
        const actions: any[] = [];

        if (row.submission?.status === SubmissionStatus.NotStarted) {
          actions.push(
            <Tooltip title="Create Submission" key="createSubmission">
              <GridActionsCellItem
                onClick={() => handleRowClick(row)}
                label="Create Submission"
                icon={<Add />}
              />
            </Tooltip>
          );
        }

        return actions;
      },
    },
  ];

  const {
    omnisearch,
    debouncedOmnisearch,
    setOmnisearch,
    getOmnisearchValue,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
    columns: omnisearchColumns,
    sortModel,
    pageSize,
    page,
  } = useOmnisearchDatagrid({
    persistenceKey: omnisearchPersistenceKey,
    isURLDriven: true,
    columns,
    initialSortModel: [
      { field: "dueDate", sort: "asc" },
      { field: "title", sort: "asc" },
    ],
    initialPageSize: 50,
  });

  const handleReadyForActionChange = useCallback(
    (checked: boolean) => {
      if (checked && currentUserEmail) {
        editOmnisearchKeyValues([["readyForAction", currentUserEmail]]);
      } else {
        removeOmnisearchKeyValue("readyForAction");
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue, currentUserEmail]
  );

  const handleTimeFilterChange = useCallback(
    (newTimeFilter: string | null) => {
      if (newTimeFilter && newTimeFilter !== "") {
        editOmnisearchKeyValues([["timeFilter", newTimeFilter]]);
      } else {
        removeOmnisearchKeyValue("timeFilter");
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  const handleViewUpcoming = useCallback(() => {
    editOmnisearchKeyValues([["timeFilter", "upcoming"]]);
  }, [editOmnisearchKeyValues]);

  const clearOmnisearch = useCallback(() => {
    setOmnisearch("");
  }, [setOmnisearch]);

  const timeFilter = getOmnisearchValue("timeFilter") || "";
  const readyForAction = !!getOmnisearchValue("readyForAction");

  // Use the same query as the grid to get loading/data state
  const search = useMemo(
    () =>
      [`tenantId:${tenantId} status:ACTIVE|OVERDUE`, debouncedOmnisearch]
        .join(" ")
        .trim(),
    [tenantId, debouncedOmnisearch]
  );

  // Clear bulk selections when filtering changes
  useEffect(() => {
    setSelectedRows([]);
    setSelectedIds([]);
  }, [omnisearch]);

  const { data, loading } = useQuery(SUBMISSION_VIRTUAL_EVENTS_QUERY, {
    variables: {
      search,
      page,
      pageSize,
      sort: [...sortModel],
    },
    fetchPolicy: "cache-and-network",
  });

  const hasData = useMemo(() => {
    if (!data) return null;
    const count = data.submissionVirtualEvents?.count ?? 0;
    return count > 0;
  }, [data]);

  const titleFilter = useMemo(() => {
    const timeFilterTitle = getTimeFilterTitle(timeFilter);
    if (timeFilterTitle) {
      return timeFilterTitle;
    } else {
      return readyForAction ? "Ready for Next Action" : "All Submissions";
    }
  }, [timeFilter, readyForAction]);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Submission Info",
        fields: ["submission.title", "submissionType", "dueDate", "documents"],
      },
      {
        name: "Location",
        fields: ["facilityId"],
      },
      {
        name: "Assignment",
        fields: ["reviewers", "signer", "staffAssignee", "waitingOn"],
      },
    ],
    []
  );

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        id="submission-filter-header"
        paddingX={3}
        paddingY={2}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack direction="row" gap={2} alignItems="flex-start">
            <FormControl variant="standard" size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="time-filter-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="time-filter-label"
                value={timeFilter}
                onChange={(e) => handleTimeFilterChange(e.target.value)}
                label="Status"
                variant="standard"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return "📄 All";
                  }
                  return TIME_FILTER_OPTIONS[
                    selected as keyof typeof TIME_FILTER_OPTIONS
                  ];
                }}
              >
                <MenuItem value="">📄 All</MenuItem>
                {Object.entries(TIME_FILTER_OPTIONS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={readyForAction}
                  onChange={(e) => handleReadyForActionChange(e.target.checked)}
                  color="primary"
                />
              }
              label="Ready for next action"
            />
          </Stack>
          {isStaff && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddSubmission}
              color="secondary"
            >
              Create Submission
            </Button>
          )}
        </Stack>
      </Stack>
      <Stack
        id="submission-list"
        p={2}
        sx={{
          background: theme.palette.grey50.main,
          minHeight: "calc(100vh - 200px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            pb: 3,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
            >
              <Box>
                <Typography
                  variant="h5"
                  component="h5"
                  color="text.primary"
                  pb={1}
                >
                  {titleFilter}
                </Typography>
                <Typography fontSize={12} component="p">
                  General waste reporting submissions. Use the search and
                  filters to find specific submissions.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {!hasData && !loading && (
            <SubmissionsEmptyState
              timeFilter={timeFilter}
              readyForAction={readyForAction}
              isStaff={isStaff}
              onCreateSubmission={handleAddSubmission}
              onViewUpcoming={handleViewUpcoming}
              omnisearch={omnisearch}
              onClearSearch={clearOmnisearch}
            />
          )}

          <Box
            /**
             * This needs to be mounted in the DOM even when there is no data to display.
             *
             * This is because the the `OmnisearchDataGrid` component sets the `initialFilters`
             * everytime it gets mounted.
             *
             * This causes finicky behavior when updating the the status dropdown where you select
             * an option and it immediately switches away from your selection to the `initialFilters`
             */
            sx={{
              display: hasData || loading ? "block" : "none",
            }}
          >
            <OmnisearchDataGrid
              persistenceKey={omnisearchPersistenceKey}
              isURLDriven
              initialFilters={[{ field: "timeFilter", value: "in-progress" }]}
              columns={omnisearchColumns}
              dataQuery={SUBMISSION_VIRTUAL_EVENTS_QUERY}
              defaultSearch={`tenantId:${tenantId} status:ACTIVE|OVERDUE`}
              getItems={(data) => data.submissionVirtualEvents.items}
              getCount={(data) => data.submissionVirtualEvents.count ?? 0}
              checkboxSelection={isStaff}
              selectedIds={isStaff ? selectedIds : undefined}
              onSelectedRowsChanged={
                isStaff ? (rows) => setSelectedRows(rows) : undefined
              }
              onSelectedIdsChanged={
                isStaff ? (ids) => setSelectedIds(ids as string[]) : undefined
              }
              onRowClick={({ row }) => handleRowClick(row)}
              commandButtons={
                isStaff
                  ? [
                      <SubmissionBulkActionsMenu
                        key="bulk-actions"
                        submissions={selectedRows}
                        onClearSelections={() => {
                          setSelectedRows([]);
                          setSelectedIds([]);
                        }}
                      />,
                    ]
                  : []
              }
              initialPageSize={25}
              rowHeight={"auto"}
              columnVisibilityModel={{
                notes: false,
                waitingOn: false,
                ...(isMobile && {
                  facilities: false,
                  tags: false,
                }),
              }}
              additionalFilterColumns={[
                {
                  header: "Facility",
                  key: "facilityId",
                  filterKeyType: "multipleFacility",
                },
                {
                  header: "Attachment Name",
                  key: "documents",
                  filterKeyType: "text",
                },
                {
                  header: "Submission Type",
                  key: "submissionType",
                  filterKeyType: "text",
                },
                {
                  header: "Due Date",
                  key: "dueDate",
                  filterKeyType: "date",
                  dateRangeShortcuts: true,
                },
                {
                  header: "Reviewer",
                  key: "reviewers",
                  filterKeyType: "text",
                },
                {
                  header: "Signer",
                  key: "signer",
                  filterKeyType: "text",
                },
                {
                  header: "Encamp Assignee",
                  key: "staffAssignee",
                  filterKeyType: "text",
                },
                {
                  header: "Waiting On",
                  key: "waitingOn",
                  filterKeyType: "multipleUser",
                },
              ]}
              excludeFilterColumns={[
                "submissionType",
                "dueDate",
                "status",
                "documents",
                "reviewers",
                "signer",
                "staffAssignee",
                "facilityName",
                "title",
                "facilityId",
                "deadline",
                "facilities",
                "submission.status",
                "waitingOn",
              ]}
              filterGroups={filterGroups}
              tenantId={tenantId}
            />
          </Box>
        </Paper>
      </Stack>
    </Stack>
  );
}
